import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Row, Col, Modal, ModalBody, ModalFooter, ModalHeader, Button, ButtonGroup, Badge, Input, Label, Spinner,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { GetData, PostData, DeleteData } from "../../services/api";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const CATEGORIES = [
  { value: "UTILITY", label: "Utilidad (transaccional)" },
  { value: "MARKETING", label: "Marketing (promocional)" },
  { value: "AUTHENTICATION", label: "Autenticación (códigos)" },
];
const LANGUAGES = [
  { value: "es_MX", label: "Español (México)" },
  { value: "es", label: "Español" },
  { value: "en_US", label: "Inglés (EE. UU.)" },
  { value: "en", label: "Inglés" },
];

const statusColor = (s) => {
  const v = String(s || "").toUpperCase();
  if (v === "APPROVED") return "success";
  if (v === "REJECTED" || v === "DISABLED") return "danger";
  if (v === "PENDING" || v === "IN_APPEAL" || v === "PENDING_DELETION") return "warning";
  return "secondary";
};

// Atributos del contacto del CRM que se pueden mapear a las variables (token → se sustituye al enviar).
const CONTACT_ATTRS = [
  { value: "", label: "Valor fijo (usa el ejemplo)" },
  { value: "contactName", label: "Nombre del contacto" },
  { value: "contactEmail", label: "Correo" },
  { value: "contactPhone", label: "Teléfono" },
  { value: "company", label: "Empresa" },
  { value: "createdBy", label: "Campaña / origen" },
];
const ATTR_SAMPLE = {
  contactName: "María López", contactEmail: "maria@correo.com",
  contactPhone: "+52 55 1234 5678", company: "Acme S.A. de C.V.", createdBy: "Clase gratuita",
};
const EMOJIS = ["😀", "😉", "🙌", "🎉", "✅", "🔥", "💬", "📅", "⏰", "📍", "🎁", "👍", "❤️", "✨", "📌", "🙏"];

// Renderiza el texto. Con `sampleMap` sustituye {{n}} por el dato de ejemplo (simulación real);
// sin él, muestra las variables como chips. Respeta saltos de línea.
const renderBody = (text, sampleMap) => {
  if (!text) return <span style={{ color: "#98a3a3" }}>Escribe el cuerpo del mensaje…</span>;
  const parts = String(text).split(/(\{\{\s*\d+\s*\}\})/g);
  return parts.map((p, i) => {
    const m = p.match(/^\{\{\s*(\d+)\s*\}\}$/);
    if (m) {
      if (sampleMap) {
        const val = sampleMap[m[1]];
        return <span key={i} style={{ fontWeight: 600 }}>{val || `[dato ${m[1]}]`}</span>;
      }
      return <span key={i} style={{ background: "#d9fdd3", color: "#075e54", borderRadius: 4, padding: "0 4px", fontWeight: 600 }}>{p}</span>;
    }
    return p.split("\n").map((line, j, arr) => (
      <React.Fragment key={`${i}-${j}`}>{line}{j < arr.length - 1 ? <br /> : null}</React.Fragment>
    ));
  });
};

// Mockup estilo iPhone + WhatsApp mostrando la plantilla como un mensaje entrante real.
const WhatsAppPreview = ({ header, body, footer, buttons, brandName, sampleMap }) => {
  const now = new Date();
  const hh = now.getHours() % 12 || 12;
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ampm = now.getHours() >= 12 ? "p.m." : "a.m.";
  const time = `${hh}:${mm} ${ampm}`;
  const btns = buttons || [];
  return (
    <div style={{
      width: 300, margin: "0 auto", borderRadius: 40, background: "#000",
      padding: 10, boxShadow: "0 12px 40px rgba(0,0,0,.35)",
    }}>
      <div style={{ borderRadius: 32, overflow: "hidden", background: "#e5ddd5", position: "relative", height: 560 }}>
        <div style={{ position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)", width: 130, height: 22, background: "#000", borderBottomLeftRadius: 14, borderBottomRightRadius: 14, zIndex: 3 }} />
        <div style={{ background: "#075e54", color: "#fff", padding: "26px 12px 10px", display: "flex", alignItems: "center", gap: 10 }}>
          <i className="ri-arrow-left-s-line" style={{ fontSize: 22 }} />
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#25d366", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700 }}>
            <i className="ri-user-3-fill" />
          </div>
          <div style={{ lineHeight: 1.1 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{brandName || "Tu empresa"}</div>
            <div style={{ fontSize: 11, opacity: .85 }}>en línea</div>
          </div>
        </div>
        <div style={{
          height: 560 - 64, padding: "14px 10px", overflowY: "auto",
          backgroundColor: "#e5ddd5",
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Cg fill='%23d7cfc4' fill-opacity='0.4'%3E%3Ccircle cx='6' cy='6' r='1.5'/%3E%3Ccircle cx='26' cy='18' r='1.5'/%3E%3Ccircle cx='14' cy='30' r='1.5'/%3E%3C/g%3E%3C/svg%3E\")",
        }}>
          <div style={{ maxWidth: "88%" }}>
            {/* burbuja entrante */}
            <div style={{ position: "relative", background: "#fff", borderRadius: 8, borderTopLeftRadius: 0, overflow: "hidden", boxShadow: "0 1px 1px rgba(0,0,0,.12)" }}>
              {/* Encabezado imagen */}
              {header?.type === "image" && (
                header.link
                  ? <img src={header.link} alt="" style={{ display: "block", width: "100%", maxHeight: 160, objectFit: "cover" }} onError={(e) => { e.target.style.display = "none"; }} />
                  : <div style={{ height: 130, background: "#dfe7e5", display: "flex", alignItems: "center", justifyContent: "center", color: "#8aa0a0" }}><i className="ri-image-line" style={{ fontSize: 34 }} /></div>
              )}
              <div style={{ padding: "6px 8px 4px", fontSize: 13.5, color: "#111b21", lineHeight: 1.35 }}>
                {/* Encabezado texto */}
                {header?.type === "text" && header.text ? <div style={{ fontWeight: 700, marginBottom: 3 }}>{header.text}</div> : null}
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{renderBody(body, sampleMap)}</div>
                {footer ? <div style={{ color: "#667781", fontSize: 11, marginTop: 4 }}>{footer}</div> : null}
                <div style={{ textAlign: "right", color: "#667781", fontSize: 10.5, marginTop: 2 }}>{time}</div>
              </div>
              {/* Botones dentro de la burbuja (si son ≤? WhatsApp los pone debajo con divisor) */}
              {btns.length > 0 && (
                <div style={{ borderTop: "1px solid #e9edef" }}>
                  {btns.map((b, i) => (
                    <div key={i} style={{ borderTop: i === 0 ? "none" : "1px solid #e9edef", padding: "8px", textAlign: "center", color: "#00a5f4", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                      <i className={b.subtype === "URL" ? "ri-external-link-line" : "ri-reply-line"} style={{ fontSize: 15 }} />
                      {b.text || (b.subtype === "URL" ? "Enlace" : "Respuesta")}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatsAppTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [numbers, setNumbers] = useState([]); // [{ phoneNumberId, number, wabaId }]
  const [selectedId, setSelectedId] = useState(""); // phoneNumberId de la línea elegida
  const connected = numbers.find((n) => n.phoneNumberId === selectedId) || numbers[0] || null;
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSample, setShowSample] = useState(true);
  const bodyRef = React.useRef(null);
  const emptyForm = { name: "", category: "UTILITY", language: "es_MX", header: { type: "none", text: "", link: "" }, body: "", footer: "", buttons: [], vars: {} };
  const [form, setForm] = useState(emptyForm);

  // Variables detectadas en el cuerpo, únicas y ordenadas (["1","2",...]).
  const detectedVars = [...new Set((form.body.match(/\{\{\s*(\d+)\s*\}\}/g) || []).map((m) => m.replace(/\D/g, "")))].sort((a, b) => Number(a) - Number(b));
  const getVar = (n) => form.vars[n] || { source: "", sample: "" };
  const updateVar = (n, patch) => setForm((f) => ({ ...f, vars: { ...f.vars, [n]: { ...getVar(n), ...patch } } }));
  const sampleFor = (n) => { const v = getVar(n); return v.sample || ATTR_SAMPLE[v.source] || `dato ${n}`; };
  const sampleMap = detectedVars.reduce((acc, n) => { acc[n] = sampleFor(n); return acc; }, {});

  const insertEmoji = (emo) => {
    const el = bodyRef.current;
    if (!el) { setForm((f) => ({ ...f, body: f.body + emo })); return; }
    const start = el.selectionStart ?? form.body.length;
    const end = el.selectionEnd ?? form.body.length;
    const next = form.body.slice(0, start) + emo + form.body.slice(end);
    setForm((f) => ({ ...f, body: next }));
    requestAnimationFrame(() => { try { el.focus(); el.selectionStart = el.selectionEnd = start + emo.length; } catch (_) {} });
  };

  const insertVariable = () => {
    const nums = detectedVars.map(Number);
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    insertEmoji(`{{${next}}}`);
  };

  const setHeaderType = (type) => setForm((f) => ({ ...f, header: { ...f.header, type } }));
  const addButton = (subtype) => setForm((f) => (f.buttons.length >= 3 ? f : { ...f, buttons: [...f.buttons, { subtype, text: "", url: "" }] }));
  const updateButton = (i, patch) => setForm((f) => ({ ...f, buttons: f.buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  const removeButton = (i) => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, idx) => idx !== i) }));

  const loadTemplates = useCallback(async () => {
    try {
      const r = await GetData("/templates/whatsapp");
      setTemplates(Array.isArray(r?.data) ? r.data : []);
    } catch (e) {
      setTemplates([]);
    }
  }, []);

  const loadConnected = useCallback(async () => {
    try {
      const r = await GetData("/whatsapp/connect/status");
      const list = r?.data?.numbers || [];
      setNumbers(list);
      if (list[0]) setSelectedId((prev) => prev || list[0].phoneNumberId);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([loadTemplates(), loadConnected()]);
      setLoading(false);
    })();
  }, [loadTemplates, loadConnected]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await PostData("/whatsapp/templates/sync", {});
      toast.success(`Plantillas sincronizadas: ${r?.data?.synced ?? 0}.`);
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "No se pudo sincronizar (¿WhatsApp conectado?).");
    } finally {
      setSyncing(false);
    }
  };

  const submit = async () => {
    const name = form.name.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!name) return toast.error("Ponle un nombre a la plantilla.");
    if (!form.body.trim()) return toast.error("El cuerpo no puede estar vacío.");
    if (!connected?.phoneNumberId) return toast.error("Primero conecta una cuenta de WhatsApp.");

    if (form.header.type === "image" && !form.header.link.trim()) return toast.error("Pon la URL de la imagen del encabezado.");
    const badBtn = form.buttons.find((b) => !b.text.trim() || (b.subtype === "URL" && !b.url.trim()));
    if (badBtn) return toast.error("Completa el texto (y URL) de cada botón.");

    // Ejemplos (en orden) para la aprobación de Meta y mapeo variable→atributo del contacto.
    const examples = detectedVars.map((n) => sampleFor(n));
    const varMap = detectedVars.reduce((acc, n) => { acc[n] = getVar(n).source || ""; return acc; }, {});

    setSaving(true);
    try {
      const params = { body: { type: "text", text: form.body.trim() } };
      if (examples.length) params.body.example = examples; // valores de muestra para {{1}}, {{2}}…
      if (form.header.type === "text" && form.header.text.trim()) params.header = { type: "text", text: form.header.text.trim() };
      if (form.header.type === "image" && form.header.link.trim()) params.header = { type: "image", link: form.header.link.trim() };
      if (form.footer.trim()) params.footer = { type: "text", text: form.footer.trim() };
      if (form.buttons.length) {
        params.buttons = form.buttons.map((b) => (b.subtype === "URL"
          ? { type: "URL", text: b.text.trim(), url: b.url.trim() }
          : { type: "QUICK_REPLY", text: b.text.trim() }));
      }

      await PostData("/template/whatsapp", {
        templateName: name,
        category: form.category,
        type: "whatsapp",
        template: { language: form.language },
        from: connected.number,
        phoneId: connected.phoneNumberId,
        business_account_id: connected.wabaId,
        params,
        varMap, // mapeo {{n}} → atributo del contacto (para pre-llenar la automatización)
      });
      toast.success("Plantilla enviada a Meta para aprobación.");
      setModal(false);
      setForm(emptyForm);
      await handleSync(); // refresca la lista + estados
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || "No se pudo crear la plantilla.");
    } finally {
      setSaving(false);
    }
  };

  // ── Probar envío ──
  const [testRow, setTestRow] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testVars, setTestVars] = useState([]);
  const [testing, setTesting] = useState(false);

  const bodyTextOf = (row) => ((row?.params?.components || []).find((c) => String(c.type).toUpperCase() === "BODY") || {}).text || "";
  const varsOf = (row) => (bodyTextOf(row).match(/\{\{\s*\d+\s*\}\}/g) || []).length;

  const openTest = (row) => {
    setTestRow(row);
    setTestTo("");
    setTestVars(Array(varsOf(row)).fill(""));
  };
  const sendTest = async () => {
    if (!testTo.replace(/\D/g, "")) return toast.error("Pon el número destino con lada (ej. 5215512345678).");
    setTesting(true);
    try {
      const r = await PostData("/whatsapp/send", { to: testTo, templateId: testRow.id, variables: testVars });
      toast.success(`Mensaje enviado ✓ (id ${r?.data?.messageId || "-"})`);
      setTestRow(null);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || "No se pudo enviar.");
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Eliminar la plantilla "${row.name}"? Se borra en Meta y del CRM.`)) return;
    try {
      await DeleteData(`/whatsapp/templates/${encodeURIComponent(row.id)}`);
      toast.success("Plantilla eliminada.");
      await loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "No se pudo eliminar.");
    }
  };

  const columns = [
    { name: <span className="fw-bold">Nombre</span>, selector: (r) => r.name, sortable: true, grow: 2 },
    { name: <span className="fw-bold">Categoría</span>, selector: (r) => r.category || "—", sortable: true },
    { name: <span className="fw-bold">Idioma</span>, selector: (r) => r.language || "—" },
    {
      name: <span className="fw-bold">Estado</span>,
      cell: (r) => <Badge color={statusColor(r.status)}>{r.status || "sin sincronizar"}</Badge>,
      sortable: true,
    },
    {
      name: <span className="fw-bold">Acción</span>,
      cell: (r) => (
        <div className="d-flex gap-1">
          {String(r.status || "").toUpperCase() === "APPROVED" && (
            <Button size="sm" color="outline-success" onClick={() => openTest(r)} title="Probar envío">
              <i className="ri-send-plane-line" />
            </Button>
          )}
          <Button size="sm" color="outline-danger" onClick={() => handleDelete(r)} title="Eliminar">
            <i className="ri-delete-bin-line" />
          </Button>
        </div>
      ),
      right: true,
      width: "130px",
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="WhatsApp" breadcrumbItem="Plantillas de WhatsApp" />

        {!loading && !connected && (
          <Row className="mb-3"><Col>
            <div className="alert alert-warning mb-0">
              No hay una cuenta de WhatsApp conectada. Ve a <b>Plantillas → Conectar WhatsApp</b> primero.
            </div>
          </Col></Row>
        )}

        <Row className="g-2 mb-3">
          <Col className="d-flex gap-2 justify-content-end">
            <Button color="light" onClick={handleSync} disabled={syncing}>
              {syncing ? "Sincronizando…" : "Sincronizar"}
            </Button>
            <Button color="success" onClick={() => setModal(true)} disabled={!connected}>
              <i className="ri-add-line align-bottom me-1"></i> Nueva plantilla
            </Button>
          </Col>
        </Row>

        <Row>
          <Col>
            {loading ? (
              <Spinner />
            ) : (
              <DataTable
                data={templates}
                columns={columns}
                pagination
                noDataComponent={<span className="py-4">Sin plantillas. Crea una o sincroniza desde Meta.</span>}
              />
            )}
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="xl">
        <ModalHeader toggle={() => setModal(false)}>Nueva plantilla de WhatsApp</ModalHeader>
        <ModalBody>
          {/* Selector de línea: la plantilla se crea en la WABA de la línea elegida. */}
          <Row className="mb-3">
            <Col md={7}>
              <Label className="mb-1">Línea de WhatsApp</Label>
              {numbers.length > 1 ? (
                <Input type="select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                  {numbers.map((n) => (
                    <option key={n.phoneNumberId} value={n.phoneNumberId}>{n.number} · WABA {n.wabaId}</option>
                  ))}
                </Input>
              ) : (
                <div className="form-control-plaintext">
                  {connected ? <b>{connected.number}</b> : <span className="text-muted">Sin línea conectada</span>}
                </div>
              )}
              {numbers.length > 1 && <small className="text-muted">La plantilla se crea en la WABA de esta línea (las líneas que comparten WABA comparten plantillas).</small>}
            </Col>
          </Row>
          <Row>
            {/* ── Formulario ── */}
            <Col lg={7}>
              <Row className="g-3">
                <Col md={6}>
                  <Label>Nombre *</Label>
                  <Input value={form.name} maxLength={60}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="ej. recordatorio_cita" />
                  <small className="text-muted">Solo minúsculas, números y guión bajo (se normaliza).</small>
                </Col>
                <Col md={3}>
                  <Label>Categoría *</Label>
                  <Input type="select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label>Idioma *</Label>
                  <Input type="select" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                    {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </Input>
                </Col>
                <Col md={12}>
                  <Label className="text-uppercase text-muted" style={{ fontSize: 11, letterSpacing: ".04em" }}>Encabezado (opcional)</Label>
                  <div>
                    <ButtonGroup size="sm">
                      {[["none", "Ninguno"], ["text", "Texto"], ["image", "Imagen"]].map(([v, l]) => (
                        <Button key={v} color={form.header.type === v ? "success" : "light"} onClick={() => setHeaderType(v)}>{l}</Button>
                      ))}
                    </ButtonGroup>
                    {form.header.type === "text" && (
                      <Input className="mt-2" maxLength={60} value={form.header.text}
                        onChange={(e) => setForm({ ...form, header: { ...form.header, text: e.target.value } })}
                        placeholder="Título del mensaje (máx 60)" />
                    )}
                    {form.header.type === "image" && (
                      <Input className="mt-2" value={form.header.link}
                        onChange={(e) => setForm({ ...form, header: { ...form.header, link: e.target.value } })}
                        placeholder="URL pública de la imagen (https://…)" />
                    )}
                  </div>
                </Col>
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center">
                    <Label className="mb-0">Cuerpo del mensaje *</Label>
                    <Button size="sm" color="light" onClick={insertVariable}><i className="ri-braces-line me-1" />Insertar variable</Button>
                  </div>
                  <Input innerRef={bodyRef} type="textarea" rows={6} value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    placeholder={"Hola {{1}}, tu cita es el {{2}}. ¡Te esperamos! 🎉"} />
                  <div className="mt-1" style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {EMOJIS.map((e) => (
                      <button key={e} type="button" className="btn btn-sm btn-light py-0 px-1" style={{ fontSize: 16, lineHeight: 1.4 }} onClick={() => insertEmoji(e)}>{e}</button>
                    ))}
                  </div>
                </Col>

                {detectedVars.length > 0 && (
                  <Col md={12}>
                    <Label className="text-uppercase text-muted" style={{ fontSize: 11, letterSpacing: ".04em" }}>Datos de las variables</Label>
                    <div className="border rounded p-2">
                      {detectedVars.map((n) => (
                        <div key={n} className="d-flex gap-2 align-items-center mb-2">
                          <Badge color="success" style={{ width: 46, flex: "0 0 auto" }}>{`{{${n}}}`}</Badge>
                          <Input bsSize="sm" type="select" value={getVar(n).source}
                            onChange={(e) => updateVar(n, { source: e.target.value })} style={{ maxWidth: 210, flex: "0 0 auto" }}>
                            {CONTACT_ATTRS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                          </Input>
                          <Input bsSize="sm" value={getVar(n).sample}
                            onChange={(e) => updateVar(n, { sample: e.target.value })}
                            placeholder={`Ejemplo (${ATTR_SAMPLE[getVar(n).source] || "texto de muestra"})`} />
                        </div>
                      ))}
                      <small className="text-muted">
                        El <b>atributo</b> es lo que se envía del contacto en la automatización; el <b>ejemplo</b> es para la vista previa y la aprobación de Meta.
                      </small>
                    </div>
                  </Col>
                )}
                <Col md={12}>
                  <Label>Pie de página (opcional)</Label>
                  <Input value={form.footer} maxLength={60}
                    onChange={(e) => setForm({ ...form, footer: e.target.value })}
                    placeholder="ej. Responde STOP para cancelar" />
                </Col>

                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center">
                    <Label className="text-uppercase text-muted mb-0" style={{ fontSize: 11, letterSpacing: ".04em" }}>Botones (opcional)</Label>
                    <div className="d-flex gap-1">
                      <Button size="sm" color="light" disabled={form.buttons.length >= 3} onClick={() => addButton("QUICK_REPLY")}>+ Respuesta rápida</Button>
                      <Button size="sm" color="light" disabled={form.buttons.length >= 3} onClick={() => addButton("URL")}>+ Enlace</Button>
                    </div>
                  </div>
                  {form.buttons.map((b, i) => (
                    <div key={i} className="d-flex gap-2 align-items-center mt-2">
                      <Badge color={b.subtype === "URL" ? "info" : "secondary"} style={{ width: 92, flex: "0 0 auto" }}>
                        {b.subtype === "URL" ? "Enlace" : "Resp. rápida"}
                      </Badge>
                      <Input bsSize="sm" value={b.text} maxLength={25}
                        onChange={(e) => updateButton(i, { text: e.target.value })}
                        placeholder="Texto del botón (máx 25)" />
                      {b.subtype === "URL" && (
                        <Input bsSize="sm" value={b.url}
                          onChange={(e) => updateButton(i, { url: e.target.value })}
                          placeholder="https://…" />
                      )}
                      <Button size="sm" color="outline-danger" onClick={() => removeButton(i)}><i className="ri-close-line" /></Button>
                    </div>
                  ))}
                  {form.buttons.length >= 3 && <small className="text-muted">Máximo 3 botones.</small>}
                </Col>
              </Row>
              <div className="alert alert-info mt-3 mb-0">
                La plantilla se envía a <b>Meta para aprobación</b>. Solo podrás usarla en automatizaciones cuando quede <b>APPROVED</b> (usa «Sincronizar» para actualizar el estado).
              </div>
            </Col>

            {/* ── Vista previa iPhone/WhatsApp ── */}
            <Col lg={5} className="mt-4 mt-lg-0">
              <div className="d-flex justify-content-center align-items-center gap-2 mb-2">
                <span className="text-muted" style={{ fontSize: 12 }}>Vista previa</span>
                {detectedVars.length > 0 && (
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input" type="checkbox" id="wa-sample" checked={showSample} onChange={(e) => setShowSample(e.target.checked)} />
                    <label className="form-check-label" htmlFor="wa-sample" style={{ fontSize: 12 }}>Datos de ejemplo</label>
                  </div>
                )}
              </div>
              <WhatsAppPreview header={form.header} body={form.body} footer={form.footer} buttons={form.buttons}
                sampleMap={showSample ? sampleMap : null} brandName={connected?.number} />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>Cerrar</Button>
          <Button color="success" onClick={submit} disabled={saving}>
            {saving ? "Enviando…" : "Crear y enviar a aprobación"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal: probar envío de una plantilla aprobada */}
      <Modal isOpen={!!testRow} toggle={() => setTestRow(null)} centered>
        <ModalHeader toggle={() => setTestRow(null)}>Probar envío · {testRow?.name}</ModalHeader>
        <ModalBody>
          <Label>Número destino (con lada)</Label>
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="5215512345678" />
          <small className="text-muted">Solo dígitos, con código de país (México = 52). Enviará desde tu línea conectada.</small>
          {testVars.length > 0 && (
            <div className="mt-3">
              <Label className="text-uppercase text-muted" style={{ fontSize: 11, letterSpacing: ".04em" }}>Valores de las variables</Label>
              {testVars.map((v, i) => (
                <div key={i} className="d-flex gap-2 align-items-center mb-2">
                  <Badge color="success" style={{ width: 46, flex: "0 0 auto" }}>{`{{${i + 1}}}`}</Badge>
                  <Input bsSize="sm" value={v}
                    onChange={(e) => setTestVars(testVars.map((x, idx) => (idx === i ? e.target.value : x)))}
                    placeholder={`Valor para {{${i + 1}}}`} />
                </div>
              ))}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setTestRow(null)}>Cerrar</Button>
          <Button color="success" onClick={sendTest} disabled={testing}>{testing ? "Enviando…" : "Enviar prueba"}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default WhatsAppTemplates;
